import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import validator from 'validator';

import OnboardingShell, { useOnboardingStyles } from '../OnboardingShell';
import GenericState from '../../../MyComponents/GenericLoadingErrorState/GenericState';
import userApi from '../../../Api/userApi';
import store from '../../../redux/store';
import { showError, updateUserPreferences } from '../../../redux/actions';
import { cx } from '../../Home/HomeDashboard/dashboardStyles';

/**
 * Asks for (or confirms) the account's email before a paid plan: sends the
 * activation link, then polls the profile until the address is validated.
 */
const ValidateEmail = ({ profile, validateCallback }) => {
    const classes = useOnboardingStyles();
    const intl = useIntl();
    const [isStarting, setIsStarting] = useState(false);
    const [sentEmail, setSentEmail] = useState(false);
    const [email, setEmail] = useState('');
    const interval = useRef({ func: null });

    useEffect(() => {
        return function () {
            if (interval.current.func) {
                clearInterval(interval.current.func);
            }
        };
    }, []);

    useEffect(() => {
        if (profile?.preferences?.email) {
            if (profile?.preferences?.emailIsValid && validateCallback) {
                validateCallback();
            } else if (validator.isEmail(profile.preferences.email)) {
                setEmail(profile.preferences.email);
            }
        }
    }, [profile, validateCallback]);

    const updateEmail = (event) => {
        event.preventDefault();
        if (!validator.isEmail(email)) return;
        setIsStarting(true);
        userApi
            .updateEmail({ email })
            .then(() => {
                setSentEmail(true);
                interval.current.func = setInterval(() => {
                    userApi.getExtendedProfile().then((response) => {
                        if (response.data.preferences.email === email && response.data.preferences.emailIsValid) {
                            store.dispatch(updateUserPreferences(response.data.preferences));
                            if (interval.current.func) {
                                clearInterval(interval.current.func);
                                interval.current.func = null;
                            }
                        }
                    });
                }, 1000);
                window.localStorage.setItem('pathBeforeLogin', window.location.pathname + window.location.search);
            })
            .catch((err) => {
                setIsStarting(false);
                store.dispatch(showError('GENERIC_ERROR'));
                console.error('Unable to update email', err);
            });
    };

    let statusId;
    if (sentEmail) {
        statusId = 'profile.validEmail.emailSent';
    } else if (profile?.preferences?.emailIsValid) {
        statusId = 'profile.validEmail.emailValidated.description';
    } else {
        statusId = 'profile.validEmail.emailNotValidated.description';
    }
    const buttonId = profile?.preferences?.emailIsValid ? 'profile.validEmail.activate' : 'profile.validEmail.change';

    return (
        <OnboardingShell
            title={<FormattedMessage id="onboarding.verifyEmail.updateTitle" />}
            lead={<FormattedMessage id={statusId} />}
        >
            <form className={cx(classes.card, classes.centered)} onSubmit={updateEmail} noValidate>
                <label className={classes.field}>
                    <span className={classes.label}>{intl.formatMessage({ id: 'contactUs.email' })}</span>
                    <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        disabled={isStarting || sentEmail}
                        onChange={(event) => setEmail(event.target.value)}
                        className={classes.input}
                    />
                </label>
                <div className={classes.actions}>
                    {isStarting && !sentEmail ? <GenericState isLoading={true} inline={true} /> : null}
                    {!isStarting ? (
                        <button type="submit" disabled={!validator.isEmail(email)} className={classes.primary}>
                            <FormattedMessage id={buttonId} />
                            <span aria-hidden="true">→</span>
                        </button>
                    ) : null}
                </div>
                {sentEmail ? (
                    <p className={classes.trust}>
                        <FormattedMessage id="profile.validEmail.emailSent" />
                    </p>
                ) : null}
            </form>
        </OnboardingShell>
    );
};

export default ValidateEmail;
